export const LAUNCHER_ICON = {
  CHAT: 'chat',
  SPARKLE: 'sparkle',
  QUESTION: 'question',
  CUSTOM: 'custom',
}

export const LAUNCHER_ICON_LABEL = {
  [LAUNCHER_ICON.CHAT]: 'Chat bubble',
  [LAUNCHER_ICON.SPARKLE]: 'Sparkle',
  [LAUNCHER_ICON.QUESTION]: 'Question mark',
  [LAUNCHER_ICON.CUSTOM]: 'Custom image',
}

export const POSITION = {
  RIGHT: 'right',
  LEFT: 'left',
}

export const THEME = {
  LIGHT: 'light',
  DARK: 'dark',
  AUTO: 'auto',
}

export const PERSONA_TONE = [
  { value: 'friendly', label: 'Friendly', hint: 'Warm, conversational, uses contractions.' },
  { value: 'professional', label: 'Professional', hint: 'Polite and to the point.' },
  { value: 'playful', label: 'Playful', hint: 'Light humour, casual language.' },
  { value: 'concise', label: 'Concise', hint: 'Direct, short answers.' },
]

export const BRAND_PALETTE = [
  { value: 'orange', label: 'Orange', hex: '#FE6337' },
  { value: 'amber', label: 'Amber', hex: '#FFB52E' },
  { value: 'green', label: 'Green', hex: '#15803D' },
  { value: 'blue', label: 'Blue', hex: '#4164FE' },
  { value: 'indigo', label: 'Indigo', hex: '#4338CA' },
  { value: 'purple', label: 'Purple', hex: '#7E22CE' },
  { value: 'black', label: 'Black', hex: '#09090B' },
]

export const CONVERSATION_STATUS = {
  BOT_ACTIVE: 'bot_active',
  OPEN: 'open',
  RESOLVED: 'resolved',
  ASSIGNED: 'assigned',
}

export const CONVERSATION_STATUS_LABEL = {
  [CONVERSATION_STATUS.BOT_ACTIVE]: 'Bot active',
  [CONVERSATION_STATUS.OPEN]: 'Needs attention',
  [CONVERSATION_STATUS.RESOLVED]: 'Resolved',
  [CONVERSATION_STATUS.ASSIGNED]: 'Assigned',
}

export const CONVERSATION_STATUS_BADGE_COLOR = {
  [CONVERSATION_STATUS.BOT_ACTIVE]: 'zinc',
  [CONVERSATION_STATUS.OPEN]: 'amber',
  [CONVERSATION_STATUS.RESOLVED]: 'green',
  [CONVERSATION_STATUS.ASSIGNED]: 'blue',
}

export const RESOLVED_CHAT_RETENTION = {
  '7d': '7 days',
  '10d': '10 days',
  '15d': '15 days',
  '30d': '30 days',
  '3m': '3 months',
  '6m': '6 months',
  '1y': '1 year',
  never: 'Never',
}

export const DEFAULT_RESOLVED_CHAT_RETENTION = '3m'

export const SOURCE_KIND = {
  FAQ: 'faq',
  WEBSITE: 'website',
  DOCUMENT: 'document',
}

export const SOURCE_STATUS = {
  READY: 'ready',
  CRAWLING: 'crawling',
  QUEUED: 'queued',
  FAILED: 'failed',
}

export const SOURCE_STATUS_LABEL = {
  [SOURCE_STATUS.READY]: 'Ready',
  [SOURCE_STATUS.CRAWLING]: 'Crawling',
  [SOURCE_STATUS.QUEUED]: 'Queued',
  [SOURCE_STATUS.FAILED]: 'Failed',
}

export const SOURCE_STATUS_BADGE_COLOR = {
  [SOURCE_STATUS.READY]: 'green',
  [SOURCE_STATUS.CRAWLING]: 'blue',
  [SOURCE_STATUS.QUEUED]: 'zinc',
  [SOURCE_STATUS.FAILED]: 'red',
}

export const FAQ_EMBED_STATUS = {
  PENDING: 'pending',
  READY: 'ready',
  FAILED: 'failed',
}

export const FAQ_EMBED_STATUS_LABEL = {
  [FAQ_EMBED_STATUS.PENDING]: 'Indexing',
  [FAQ_EMBED_STATUS.READY]: 'Ready',
  [FAQ_EMBED_STATUS.FAILED]: 'Failed',
}

export const FAQ_EMBED_STATUS_BADGE_COLOR = {
  [FAQ_EMBED_STATUS.PENDING]: 'blue',
  [FAQ_EMBED_STATUS.READY]: 'green',
  [FAQ_EMBED_STATUS.FAILED]: 'red',
}

/** aria-label / title for training retry actions */
export const TRAINING_RETRY_ACTION = {
  WEBSITE: { label: 'Retry crawl', success: 'Website crawl re-queued' },
  DOCUMENT: { label: 'Retry indexing', success: 'Document indexing re-queued' },
  FAQ: { label: 'Retry indexing', success: 'FAQ indexing re-queued' },
}

export const TRIGGER_KIND = {
  IMMEDIATE: 'immediate',
  TIME: 'time',
  EXIT_INTENT: 'exit_intent',
  PATH: 'path',
}

export const TRIGGER_KIND_LABEL = {
  [TRIGGER_KIND.IMMEDIATE]: 'Open right away',
  [TRIGGER_KIND.TIME]: 'After N seconds on a page',
  [TRIGGER_KIND.EXIT_INTENT]: 'On exit intent',
  [TRIGGER_KIND.PATH]: 'On specific page paths',
}

export const HANDOFF_TRIGGER = [
  { value: 'never', label: 'Never — bot only' },
  { value: 'on_request', label: 'When the visitor asks for a human' },
  { value: 'after_unresolved', label: 'After 3 unresolved replies' },
  { value: 'always_off_hours', label: 'When outside office hours' },
]

export const DEFAULT_TAGLINE = 'We typically reply in a few minutes'

export const DEFAULT_CONFIG = {
  name: 'CodeDesign Bot',
  avatarInitials: 'CD',
  avatarUrl: null,
  disabled: false,
  tagline: DEFAULT_TAGLINE,
  greeting: '👋 Hi there! How can I help you today?',
  suggestedReplies: [
    'What does CodeDesign do?',
    'Pricing & plans',
    'Talk to a human',
  ],
  brandColor: '#5C33FF',
  position: POSITION.RIGHT,
  launcherIcon: LAUNCHER_ICON.SPARKLE,
  customLauncherUrl: null,
  theme: THEME.LIGHT,
  showPoweredBy: true,
  tone: 'friendly',
  customInstructions:
    'You are a helpful assistant for our website. Answer using the knowledge sources provided. If you do not know the answer, say so politely and offer to connect the visitor with a human teammate.',
  fallbackMessage:
    "I'm not sure about that one. Want me to connect you with a teammate?",
  attachedProjectIds: [],
  allowedOrigins: [],
  automations: {
    welcomeTrigger: { kind: TRIGGER_KIND.IMMEDIATE, delaySeconds: 5, paths: [] },
    awayMode: {
      enabled: false,
      message: "We're away right now — leave us your email and we'll get back to you.",
      timezone: 'UTC',
      hours: { from: '09:00', to: '17:00' },
    },
    leadCapture: {
      enabled: true,
      askAfterTurns: 1,
      message: "Mind sharing your email so we can follow up if needed?",
    },
    handoffTrigger: 'after_unresolved',
    cannedResponses: [
      { id: 'cr_1', shortcut: '/pricing', body: 'Here are our plans: https://codedesign.app/pricing' },
      { id: 'cr_2', shortcut: '/docs', body: 'Our docs are at https://codedesign.app/docs' },
    ],
  },
}

export const CHATBOT_TABS = [
  { key: 'inbox', label: 'Inbox', path: '/chatbot' },
  { key: 'training', label: 'Training', path: '/chatbot/training' },
  { key: 'design', label: 'Design', path: '/chatbot/design' },
  // { key: 'automations', label: 'Automations', path: '/chatbot/automations' },
  { key: 'test', label: 'Test', path: '/chatbot/test' },
  { key: 'install', label: 'Install', path: '/chatbot/install' },
  { key: 'analytics', label: 'Analytics', path: '/chatbot/analytics' },
  { key: 'settings', label: 'Settings', path: '/chatbot/settings' },
]

// Tabs for the "Manage Chatbot" page. Training is the index route of
// /chatbot/manage, the rest are nested under it.
export const MANAGE_TABS = [
  { key: 'training', label: 'Training', path: '/chatbot/manage' },
  { key: 'design', label: 'Design', path: '/chatbot/manage/design' },
  { key: 'test', label: 'Test', path: '/chatbot/manage/test' },
  { key: 'install', label: 'Install', path: '/chatbot/manage/install' },
  { key: 'analytics', label: 'Analytics', path: '/chatbot/manage/analytics' },
  { key: 'settings', label: 'Settings', path: '/chatbot/manage/settings' },
]

export const CHATBOT_TRAINING_TABS = [
  { key: 'faqs', label: 'FAQs' },
  { key: 'websites', label: 'Websites' },
  { key: 'documents', label: 'Documents' },
  { key: 'instructions', label: 'Custom instructions' },
]

function requiredEnv(key) {
  const value = import.meta.env[key]
  if (!value) throw new Error(`${key} is required`)
  return value
}

const CHATBOT_WIDGET_URL = requiredEnv('VITE_CHATBOT_WIDGET_URL').replace(/\/+$/, '')
const DEV_EMBED_URL = `${CHATBOT_WIDGET_URL}/embed.js`

export const INSTALL_SNIPPETS = [
  {
    key: 'js',
    title: 'JavaScript snippet',
    description:
      'Add this script before </body> on any website.',
    build: (botId) => `<script
  src="${DEV_EMBED_URL}"
  data-bot-id="${botId}"
  async
></script>`,
  },
  {
    key: 'wordpress',
    title: 'WordPress',
    description:
      'Paste this into a Custom HTML block or use "Insert Headers and Footers" with a plugin like "WPCode".',
    build: (botId) => `<script
  src="${DEV_EMBED_URL}"
  data-bot-id="${botId}"
  async
></script>`,
  },
  {
    key: 'shopify',
    title: 'Shopify',
    description:
      'Open Shopify Admin → Online Store → Themes → Edit Code → layout/theme.liquid. Paste before </head>.',
    build: (botId) => `<script
  src="${DEV_EMBED_URL}"
  data-bot-id="${botId}"
  async
></script>`,
  },
  {
    key: 'react',
    title: 'React',
    description:
      'Mount <Chatbot /> once at the app root, such as in App.jsx.',
    build: (botId) => `import { useEffect } from 'react'

export default function Chatbot() {
  useEffect(() => {
    const script = document.createElement('script')

    script.src = '${DEV_EMBED_URL}'
    script.async = true
    script.dataset.botId = '${botId}'

    document.body.appendChild(script)

    return () => {
      document.body.removeChild(script)
    }
  }, [])

  return null
}`,
  },
  {
    key: 'nextjs',
    title: 'Next.js',
    description:
      'After creating this client component, render <Chatbot /> once from your root layout or app shell.',
    build: (botId) => `'use client'

import Script from 'next/script'

export default function Chatbot() {
  return (
    <Script
      src="${DEV_EMBED_URL}"
      data-bot-id="${botId}"
      strategy="afterInteractive"
    />
  )
}`,
  },
]
